import { useEffect, useState, useCallback, useMemo } from 'react'
import { DRAW_BET_TYPES, DRAW_BET_TYPE_DIGITS } from '@nexbit/types'
import type { DrawBetType, DrawLotteryWithCurrent, AgentNumberRule, AgentNumberRuleInput } from '@nexbit/types'
import { ChoiceSelect } from '@/components/choice-select'
import { betChoices } from '@/lib/bet-choices'
import {
  apiDrawGetLotteries, apiAgentNumberRules, apiAgentSaveNumberRules,
  apiAgentDeleteNumberRule, apiAgentClearNumberRules, apiAgentExposure, ApiRequestError,
  type AgentExposure,
} from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircle, Ban, Loader2, Pencil, Plus, Save, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import type { Dict } from '@/lib/i18n'

/**
 * อั้นเลขของเอเย่นต์ — an agent's own number rules.
 *
 * Shaped after the admin's ปิดรับเลข screen so operators who have seen one recognise the
 * other, with two differences that matter:
 *
 *  - the ทุกงวด / งวดนี้ switch, which the admin page does not have as a first-class
 *    control. It is the difference between refusing a number forever and refusing it
 *    tonight, and it is the mistake an agent will make;
 *  - every row shows this agent's own fill next to the house's. An agent can have room
 *    under its own ceiling and still be refused because the งวด-wide pool filled, and
 *    "ทำไมลูกค้าผมแทงไม่ได้" is the first support call this feature will generate.
 */

const REFRESH_MS = 5000
const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

/** A rule keyed the way the server keys it, so a row and a rule can find each other. */
function ruleKey(betType: DrawBetType, number: string | null): string {
  return `${betType}|${number ?? '*'}`
}

/** The stake this agent (or the house) has taken on one number of the selected type. */
function stakeOf(ex: AgentExposure | null, side: 'mine' | 'house', betType: DrawBetType, number: string): number {
  const row = ex?.[side].byNumber.find((n) => n.betType === betType && n.number === number)
  return row ? Number(row.stake) : 0
}

/** Which rule decided a value — the number's own, the whole type's, or the house's. */
type Source = 'number' | 'type' | 'house'

/**
 * The value actually in force on one number, and where it came from.
 *
 * Mirrors resolveForBet in services/api: the house's value is always a candidate, so an
 * agent can only ever land on something stricter. The source travels with the value
 * because the number alone is not enough — the table used to print a blank where the
 * agent had set nothing, which reads as "then the house's applies". True only until a
 * whole-type rule exists, after which the number quietly pays something else.
 */
function strictest(house: number, type: number | null, number: number | null): { value: number; source: Source } {
  let best: { value: number; source: Source } = { value: house, source: 'house' }
  // Tried house → type → number and compared with <=, so an equal value resolves to the
  // more specific rule: an agent who set 800 on a number wants to see it named there.
  if (type !== null && type <= best.value) best = { value: type, source: 'type' }
  if (number !== null && number <= best.value) best = { value: number, source: 'number' }
  return best
}

/**
 * One value in force, with the rule that set it named beside it unless that rule is the
 * number's own. Named rather than only shaded: a tag an agent can read beats a grey they
 * would have to be told the meaning of, and it survives a screenshot sent to support.
 */
function InForce({ value, label }: { value: string; label: string | null }) {
  return (
    <>
      <span className={label === null ? 'text-foreground' : 'text-muted-foreground'}>{value}</span>
      {label !== null && <span className="ml-1.5 text-[10px] text-muted-foreground">{label}</span>}
    </>
  )
}

/** One number in a bet type's book, with what this agent and the house hold on it. */
interface SectionRow {
  number: string
  rule: AgentNumberRule | null
  mine: number
  house: number
}

/** The house's limits for one bet type, absent while the config is still loading. */
type HouseLimits = { payout: number; maxPerNumber: number } | undefined

/**
 * One bet type's book — its own heading, its own table.
 *
 * The controls that write stay at the top of the page and act on the selected type; these
 * tables are the record of what is set. Each one can hand the top controls its own type,
 * so managing a book never means hunting for its chip.
 */
function TypeSection({
  t, betType, rows, typeRule, cfg, houseCaps, selected, saving, onSelect, onToggleBlock, onDelete,
}: {
  t: Dict
  betType: DrawBetType
  rows: SectionRow[]
  typeRule: AgentNumberRule | null
  cfg: HouseLimits
  /** The house's per-number ceilings, keyed `betType|number`. Absent = the type's default. */
  houseCaps: Map<string, number>
  selected: boolean
  saving: boolean
  onSelect: () => void
  onToggleBlock: (row: SectionRow) => void
  onDelete: (id: string) => void
}) {
  const typeRate = typeRule?.payoutRate ? Number(typeRule.payoutRate) : null
  /** A value set on the number itself needs no tag — it is the one the agent typed there. */
  const srcLabel = (s: Source) =>
    s === 'number' ? null : s === 'type' ? t.agentNumberRules.fromType : t.agentNumberRules.fromHouse

  return (
    // A landmark per book: ten stacked tables are one undifferentiated run of rows to
    // anyone navigating by region rather than by eye.
    <section
      aria-label={t.draws.betTypeLabel[betType]}
      className={cn(
        'rounded-[var(--radius)] border bg-card shadow-[var(--shadow-sm)]',
        selected ? 'border-primary/60' : 'border-border',
      )}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-secondary px-4 py-2">
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
          {t.draws.betTypeLabel[betType]}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">({rows.length})</span>
        {typeRule?.blocked && (
          <span className="rounded-[var(--radius-sm)] border border-destructive/40 px-1.5 py-0.5 font-mono text-[10px] text-destructive">
            {t.agentNumberRules.blockType}
          </span>
        )}
        {typeRate !== null && (
          <span className="rounded-[var(--radius-sm)] border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {t.agentNumberRules.typePayout} {nf.format(typeRate)}
          </span>
        )}
        {selected ? (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-primary">
            {t.agentNumberRules.editingAbove}
          </span>
        ) : (
          <Button
            size="sm" variant="ghost" onClick={onSelect}
            className="ml-auto h-7 gap-1 font-mono text-[11px]"
          >
            <Pencil className="h-3 w-3" /> {t.agentNumberRules.manageType}
          </Button>
        )}
      </div>

      {/*
        min-w on the table: below that the five columns fold their headings into four-line
        stacks and the book becomes unreadable. The wrapper scrolls, so it scrolls instead.
      */}
      {rows.length === 0 ? (
        <p className="p-4 text-center font-mono text-[11px] text-muted-foreground">
          {t.agentNumberRules.typeRuleOnly}
        </p>
      ) : (
        <Table className="min-w-[560px]">
          <TableHeader>
            <TableRow>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest">
                {t.draws.limitsNumber}
              </TableHead>
              <TableHead className="text-right font-mono text-[10px] uppercase tracking-widest">
                {t.agentNumberRules.mineVsHouse}
              </TableHead>
              <TableHead className="text-right font-mono text-[10px] uppercase tracking-widest">
                {t.agentNumberRules.effCap}
              </TableHead>
              <TableHead className="text-right font-mono text-[10px] uppercase tracking-widest">
                {t.agentNumberRules.effPayout}
              </TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const ownCap = row.rule?.maxPerNumber ? Number(row.rule.maxPerNumber) : null
              const ownRate = row.rule?.payoutRate ? Number(row.rule.payoutRate) : null
              // A whole-type ceiling is deliberately not a feature (the table's CHECK
              // enforces that), so a ceiling has only two possible sources.
              //
              // The house side is the house's figure for THIS number: its own override if
              // the admin gave it one (higher or lower), else the bet type's default. Reading
              // the default here would print "1,000" beside a number the house raised to
              // 20,000 and tell the agent the wrong ceiling is in force.
              const houseCap = houseCaps.get(`${betType}|${row.number}`) ?? cfg?.maxPerNumber
              const cap = houseCap !== undefined ? strictest(houseCap, null, ownCap) : null
              const rate = cfg ? strictest(cfg.payout, typeRate, ownRate) : null
              return (
                <TableRow key={row.number} className={cn(row.rule?.blocked && 'opacity-60')}>
                  <TableCell className="font-mono text-sm font-semibold tabular-nums">
                    {row.number}
                    {row.rule?.blocked && (
                      <span className="ml-2 font-mono text-[10px] text-destructive">
                        {t.agentNumberRules.blocked}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs tabular-nums">
                    <span className="text-foreground">{nf.format(row.mine)}</span>
                    <span className="text-muted-foreground"> / {nf.format(row.house)}</span>
                  </TableCell>
                  {/*
                    Both columns state what is in force, not what this agent typed: a closed
                    number has nothing in force at all, and everything else names the rule
                    that decided it.
                  */}
                  <TableCell className="text-right font-mono text-xs tabular-nums">
                    {row.rule?.blocked || !cap
                      ? <span className="text-muted-foreground">—</span>
                      : <InForce value={nf.format(cap.value)} label={srcLabel(cap.source)} />}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs tabular-nums">
                    {row.rule?.blocked || !rate
                      ? <span className="text-muted-foreground">—</span>
                      : <InForce value={nf.format(rate.value)} label={srcLabel(rate.source)} />}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm" variant="ghost"
                        disabled={saving}
                        onClick={() => onToggleBlock(row)}
                        className="h-7 gap-1 font-mono text-[11px]"
                        aria-label={row.rule?.blocked ? t.agentNumberRules.unblock : t.agentNumberRules.block}
                      >
                        {row.rule?.blocked ? <X className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
                      </Button>
                      {row.rule && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => onDelete(row.rule!.id)}
                          className="h-7 gap-1 font-mono text-[11px] text-destructive hover:text-destructive"
                          aria-label={t.common.delete}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </section>
  )
}

export default function AgentNumberRulesPage() {
  const t = useT()

  const [lotteries, setLotteries] = useState<DrawLotteryWithCurrent[]>([])
  const [lotteryId, setLotteryId] = useState<string | null>(null)
  const [betType, setBetType] = useState<DrawBetType>('3top')
  // Rules belong to the lottery, not to a งวด: they apply to the one open now and to
  // every one after it, until the agent clears them. So there is no scope to choose —
  // that choice was the easiest mistake on this screen to make and the hardest to see.
  const [confirmClear, setConfirmClear] = useState(false)
  const [clearing, setClearing] = useState(false)

  const [rules, setRules] = useState<AgentNumberRule[]>([])
  const [houseConfig, setHouseConfig] = useState<Partial<Record<DrawBetType, {
    enabled: boolean; payout: number; minBet: number; maxBet: number; maxPerNumber: number; maxPerType: number
  }>>>({})
  // Keyed `betType|number`. Held as a map because that is how it is looked up, once per row.
  const [houseCaps, setHouseCaps] = useState<Map<string, number>>(new Map())
  const [exposure, setExposure] = useState<AgentExposure | null>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Draft state for the whole-type card and the add box.
  const [typePayout, setTypePayout] = useState('')
  const [addNumbers, setAddNumbers] = useState('')
  const [addCap, setAddCap] = useState('')
  const [addPayout, setAddPayout] = useState('')

  const lottery = lotteries.find((l) => l.id === lotteryId) ?? null
  const liveDraw = lottery?.currentDraw?.status === 'open' ? lottery.currentDraw : null
  const cfg = houseConfig[betType]
  const digits = DRAW_BET_TYPE_DIGITS[betType]
  const choices = betChoices(betType)

  // Text that means nothing in the bet type just switched to is dropped, never all of it:
  // '45' on a choice type, or 'high' left over on คู่/คี่. A select holding such a value
  // shows blank while the add buttons stay live. A 3-digit number is still fine on 3tote.
  useEffect(() => {
    setAddNumbers((cur) => {
      const parts = cur.split(/[\s,]+/).filter(Boolean)
      if (parts.length === 0) return cur
      const ok = choices
        ? parts.every((p) => choices.some((c) => c.value === p.toLowerCase()))
        : parts.every((p) => new RegExp(`^\\d{${digits}}$`).test(p))
      return ok ? cur : ''
    })
  }, [betType, digits, choices])

  // ── Load the lottery list once ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    apiDrawGetLotteries()
      .then((data) => {
        if (cancelled) return
        setLotteries(data)
        setLotteryId((prev) => prev ?? data[0]?.id ?? null)
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiRequestError ? e.message : t.common.error) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [t.common.error])

  const load = useCallback(async () => {
    if (!lotteryId) return
    try {
      const [res, ex] = await Promise.all([
        apiAgentNumberRules(lotteryId),
        // Every bet type at once, not just the one selected: the books below are shown
        // side by side, and asking per type would refetch the whole page on every chip.
        liveDraw ? apiAgentExposure(liveDraw.id) : Promise.resolve(null),
      ])
      setRules(res.rules)
      setHouseConfig(res.houseConfig)
      setHouseCaps(new Map((res.houseNumberCaps ?? []).map((c) => [`${c.betType}|${c.number}`, Number(c.maxPerNumber)])))
      setExposure(ex)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : t.common.error)
    }
  }, [lotteryId, liveDraw, t.common.error])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const id = setInterval(() => { void load() }, REFRESH_MS)
    return () => clearInterval(id)
  }, [load])

  // Pick a bet type the lottery actually offers when switching lotteries.
  useEffect(() => {
    const enabled = DRAW_BET_TYPES.filter((k) => houseConfig[k]?.enabled)
    if (enabled.length > 0 && !enabled.includes(betType)) setBetType(enabled[0]!)
  }, [houseConfig, betType])

  const byKey = useMemo(() => {
    const m = new Map<string, AgentNumberRule>()
    for (const r of rules) m.set(ruleKey(r.betType, r.number), r)
    return m
  }, [rules])

  const typeRule = byKey.get(ruleKey(betType, null)) ?? null

  // What the server holds for the whole-type payout, as a plain number or null. The effect
  // below depends on THIS, not on the rule object: the page re-reads the rules every five
  // seconds and each read builds fresh objects, so an effect keyed on the object put the
  // stored value back into the box while the agent was still typing in it.
  const storedTypePayout = typeRule?.payoutRate ? Number(typeRule.payoutRate) : null
  useEffect(() => {
    setTypePayout(storedTypePayout === null ? '' : String(storedTypePayout))
  }, [storedTypePayout])
  const typedPayout = typePayout.trim() === '' ? null : Number(typePayout)
  // Whether the add box carries a ceiling or a rate — decides which of its two buttons is live.
  const hasLimit = addCap.trim() !== '' || addPayout.trim() !== ''
  // Nothing to save until the box differs from what is held — otherwise "บันทึก" is a
  // button that does nothing, or worse, asks the server to store a rule that says nothing.
  const typeDirty = typedPayout !== storedTypePayout
  // What an untouched payout box in the add row would leave in force — the house's rate,
  // or the whole-type rate when one is set. Shown as the placeholder so the grey number
  // is the answer to "what happens if I leave this empty" rather than a second thing to
  // reason about.
  const effTypePayout = cfg ? strictest(cfg.payout, storedTypePayout, null).value : null

  /**
   * One book per bet type, instead of one list filtered by the chip at the top.
   *
   * An agent keeps 3 ตัวบน and 2 ตัวล่าง as separate books and reviews them that way. A
   * single list showed only whichever type was selected, so checking what was set meant
   * clicking through ten chips and holding the answer in your head.
   */
  const sections = useMemo(() => {
    const numbers = new Map<DrawBetType, Set<string>>()
    const present = new Set<DrawBetType>()
    const add = (bt: DrawBetType, n: string) => {
      const set = numbers.get(bt) ?? new Set<string>()
      set.add(n)
      numbers.set(bt, set)
    }
    for (const r of rules) {
      present.add(r.betType)
      if (r.number !== null) add(r.betType, r.number)
    }
    // A type carrying money but holding no rule belongs here too — that is where an agent
    // looks to decide whether it needs one.
    for (const n of exposure?.mine.byNumber ?? []) { present.add(n.betType); add(n.betType, n.number) }

    return DRAW_BET_TYPES.filter((bt) => present.has(bt)).map((bt) => ({
      betType: bt,
      typeRule: byKey.get(ruleKey(bt, null)) ?? null,
      rows: [...(numbers.get(bt) ?? [])].sort().map((number) => ({
        number,
        rule: byKey.get(ruleKey(bt, number)) ?? null,
        mine: stakeOf(exposure, 'mine', bt, number),
        house: stakeOf(exposure, 'house', bt, number),
      })),
    }))
  }, [rules, exposure, byKey])

  async function save(input: AgentNumberRuleInput[]) {
    if (!lotteryId) return
    setSaving(true)
    setActionError(null)
    try {
      await apiAgentSaveNumberRules({
        lotteryId,
        rules: input,
      })
      await load()
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setSaving(false)
    }
  }

  /**
   * Apply one rule, however the click that produced it came out.
   *
   * "Reopen this number" on a number that was only ever closed, or clearing the payout
   * box on a rule that only had a payout, leaves a rule that says nothing: not closed, no
   * ceiling, no rate. The server rightly refuses to store that — a row that asserts
   * nothing is a ghost in the list and a no-op on the bet path. But the agent's intent
   * there is plainly "no rule any more", so that is what happens: the rule is deleted.
   * Before this, each of those clicks sent the empty rule anyway and put a raw
   * validation error on screen — and a number closed outright could not be reopened
   * except through the trash icon.
   */
  async function commit(next: AgentNumberRuleInput, existing: AgentNumberRule | null) {
    const saysSomething = next.blocked || next.maxPerNumber !== null || next.payoutRate !== null
    if (saysSomething) { await save([next]); return }
    if (existing) await remove(existing.id)
  }

  async function remove(id: string) {
    setActionError(null)
    try {
      await apiAgentDeleteNumberRule(id)
      await load()
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    }
  }

  /**
   * Wipe every rule on this lottery — all bet types, the whole-type card included.
   * Only reached through the confirm dialog: it cannot be undone, and the book it
   * removes is one an agent may have spent a long time keying.
   */
  async function clearAll() {
    if (!lotteryId) return
    setClearing(true)
    setActionError(null)
    try {
      await apiAgentClearNumberRules(lotteryId)
      setConfirmClear(false)
      await load()
    } catch (e) {
      // Left open on failure so the agent sees the reason and can try again.
      setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setClearing(false)
    }
  }

  /** Add box: agents key numbers in sets, so several at once with one ceiling each. */
  async function addBatch(mode: 'close' | 'limit') {
    const parsed = addNumbers.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
    if (parsed.length === 0) return

    // Checked here so a mistyped number is answered in Thai, naming the number that is
    // wrong. The server refuses it too, but its message is written for an API client and
    // reaches this screen as raw English about one bet type.
    const bad = parsed.filter((n) => (choices
      // This type's OWN choices. A list of all four words accepted any of them for any
      // choice type, so 'high' passed here on คู่/คี่ and was refused by the server in English.
      ? !choices.some((c) => c.value === n.toLowerCase())
      : !new RegExp(`^\\d{${digits}}$`).test(n)))
    if (bad.length > 0) {
      setActionError(digits > 0
        ? t.agentNumberRules.badDigits(digits, bad.join(' '))
        : t.agentNumberRules.badChoice(bad.join(' ')))
      return
    }
    // The intent comes from WHICH BUTTON was pressed, not from which boxes happen to be
    // empty. It used to be inferred ("both empty means close it"), a rule nothing on
    // screen stated and which agents had to have explained to them.
    const cap = mode === 'limit' && addCap.trim() !== '' ? Number(addCap) : null
    const payout = mode === 'limit' && addPayout.trim() !== '' ? Number(addPayout) : null
    await save(parsed.map((number) => ({
      betType, number,
      blocked: mode === 'close',
      maxPerNumber: cap,
      payoutRate: payout,
    })))
    setAddNumbers(''); setAddCap(''); setAddPayout('')
  }

  const enabledTypes = DRAW_BET_TYPES.filter((k) => houseConfig[k]?.enabled)

  return (
    <div className="p-4 md:p-6 max-w-[1400px]">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.agentNumberRules.title}
        </span>
        <p className="mt-1 font-mono text-[11px] text-muted-foreground">{t.agentNumberRules.desc}</p>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-card p-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-80" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <>
          {/* ── Scope: lottery · bet type · ทุกงวด vs งวดนี้ ─────────────────── */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <select
              value={lotteryId ?? ''}
              onChange={(e) => { setLotteryId(e.target.value); setExposure(null) }}
              className="h-9 rounded-[var(--radius-sm)] border border-input bg-background px-2 font-mono text-sm text-foreground"
              aria-label={t.draws.lotteries}
            >
              {lotteries.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}{l.currentDraw?.status === 'open' ? ' ●' : ''}
                </option>
              ))}
            </select>

            <div className="flex flex-wrap gap-1">
              {enabledTypes.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setBetType(k)}
                  aria-pressed={betType === k}
                  className={cn(
                    'h-9 rounded-[var(--radius-sm)] border px-2.5 font-mono text-xs transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    betType === k
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground hover:bg-muted',
                  )}
                >
                  {t.draws.betTypeLabel[k]}
                </button>
              ))}
            </div>
          </div>

          {/*
            Says plainly that what is set here outlives the งวด on screen. Without this
            line the list reads like a per-draw worksheet, and an agent would expect it
            to empty itself when the draw ends.
          */}
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-[var(--radius-sm)] border border-border bg-secondary/40 p-2.5">
            {liveDraw ? (
              <span className="flex items-center gap-1.5 font-mono text-[11px] text-foreground">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" aria-hidden />
                {t.draws.drawNumber} {liveDraw.drawNumber}
              </span>
            ) : (
              <span className="font-mono text-[11px] text-muted-foreground">{t.draws.blockNoLiveDraw}</span>
            )}
            <span className="font-mono text-[11px] text-muted-foreground">
              {t.agentNumberRules.carriesOver}
            </span>
          </div>

          {actionError && <p className="mb-3 font-mono text-[11px] text-destructive">{actionError}</p>}

          <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
            {/* ── Add to the selected type ───────────────── */}
            <div className="self-start rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
              <div className="border-b border-border bg-secondary px-4 py-2.5">
                <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                  {t.agentNumberRules.addTitle} — {t.draws.betTypeLabel[betType]}
                </span>
              </div>
              {/* Add box — several numbers at once, since agents key them in sets. */}
              <div className="flex flex-wrap items-end gap-2 p-3">
                <div className="flex min-w-[180px] flex-1 flex-col gap-1">
                  <Label htmlFor="add-numbers" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {digits > 0 ? t.agentNumberRules.addNumbers(digits) : t.agentNumberRules.addChoice}
                  </Label>
                  {choices ? (
                    // สูง/ต่ำ and คู่/คี่ have two answers each: pick them, do not spell them.
                    <ChoiceSelect
                      id="add-numbers" choices={choices} allowBoth
                      value={addNumbers} onChange={setAddNumbers}
                    />
                  ) : (
                    <Input
                      id="add-numbers"
                      value={addNumbers}
                      onChange={(e) => setAddNumbers(e.target.value)}
                      placeholder={digits === 3 ? '123 456 789' : digits === 2 ? '45 67 89' : '1 2 3'}
                      className="h-9 font-mono text-sm"
                    />
                  )}
                </div>
                <div className="flex w-28 flex-col gap-1">
                  <Label htmlFor="add-cap" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {t.agentNumberRules.myCap}
                  </Label>
                  <Input
                    id="add-cap" inputMode="numeric" value={addCap}
                    onChange={(e) => setAddCap(e.target.value.replace(/[^\d]/g, ''))}
                    placeholder={cfg ? String(cfg.maxPerNumber) : ''}
                    className="h-9 font-mono text-sm tabular-nums"
                  />
                </div>
                <div className="flex w-28 flex-col gap-1">
                  <Label htmlFor="add-payout" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {t.agentNumberRules.myPayout}
                  </Label>
                  <Input
                    id="add-payout" inputMode="decimal" value={addPayout}
                    onChange={(e) => setAddPayout(e.target.value.replace(/[^\d.]/g, ''))}
                    placeholder={effTypePayout === null ? '' : String(effTypePayout)}
                    className="h-9 font-mono text-sm tabular-nums"
                  />
                </div>
                {/*
                  Exactly one of these is live at a time. Numbers alone can only mean
                  "close them"; a ceiling or a rate can only mean "limit them". Offering
                  the wrong one would be a button that does something other than it says.
                */}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={saving || addNumbers.trim() === '' || hasLimit}
                  onClick={() => void addBatch('close')}
                  className="h-9 gap-1.5 font-mono text-xs text-destructive hover:text-destructive"
                >
                  <Ban className="h-3 w-3" /> {t.agentNumberRules.closeNumbers}
                </Button>
                <Button
                  size="sm"
                  disabled={saving || addNumbers.trim() === '' || !hasLimit}
                  onClick={() => void addBatch('limit')}
                  className="h-9 gap-1.5 font-mono text-xs"
                >
                  <Plus className="h-3 w-3" /> {t.agentNumberRules.setLimit}
                </Button>
                <p className="w-full font-mono text-[10px] text-muted-foreground">
                  {t.agentNumberRules.addHint}
                </p>
              </div>

            </div>

            {/* ── Whole bet type ──────────────────────────────────────── */}
            <div className="self-start rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
              <div className="border-b border-border bg-secondary px-4 py-2.5">
                <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                  {t.agentNumberRules.wholeType} — {t.draws.betTypeLabel[betType]}
                </span>
              </div>
              <div className="space-y-3 p-4">
                <div className="flex items-baseline justify-between font-mono text-[11px]">
                  <span className="text-muted-foreground">{t.agentNumberRules.housePayout}</span>
                  <span className="tabular-nums text-foreground">{cfg ? nf.format(cfg.payout) : '—'}</span>
                </div>

                <div className="flex flex-col gap-1.5">
                  {/*
                    Named apart from the add box's "อัตราจ่ายของฉัน": two inputs with the
                    same accessible name on one page, each doing something different, are
                    indistinguishable to anyone reading the page by its labels.
                  */}
                  <Label htmlFor="type-payout" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {t.agentNumberRules.typePayout}
                  </Label>
                  <Input
                    id="type-payout"
                    inputMode="decimal"
                    value={typePayout}
                    onChange={(e) => setTypePayout(e.target.value.replace(/[^\d.]/g, ''))}
                    placeholder={cfg ? String(cfg.payout) : ''}
                    className="h-9 font-mono text-sm tabular-nums"
                  />
                  {/*
                    An agent whose rule sits above a house value that has since been cut
                    must see both numbers, or they believe they are stricter than they are.
                  */}
                  {typeRule?.payoutRate && cfg && Number(typeRule.payoutRate) > cfg.payout && (
                    <p className="font-mono text-[10px] text-warning">
                      {t.agentNumberRules.clamped(typeRule.payoutRate, String(cfg.payout))}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={saving || !cfg || !typeDirty}
                    onClick={() => void commit({
                      betType, number: null, blocked: typeRule?.blocked ?? false,
                      maxPerNumber: null,
                      payoutRate: typedPayout,
                    }, typeRule)}
                    className="h-8 flex-1 gap-1.5 font-mono text-xs"
                  >
                    <Save className="h-3 w-3" /> {t.common.save}
                  </Button>
                  <Button
                    size="sm"
                    variant={typeRule?.blocked ? 'destructive' : 'outline'}
                    disabled={saving || !cfg}
                    onClick={() => void commit({
                      betType, number: null, blocked: !typeRule?.blocked,
                      maxPerNumber: null,
                      // The STORED rate, not whatever is half-typed in the box: this button
                      // toggles closing, it should not also save an unfinished edit.
                      payoutRate: storedTypePayout,
                    }, typeRule)}
                    className="h-8 gap-1.5 font-mono text-xs"
                  >
                    <Ban className="h-3 w-3" />
                    {typeRule?.blocked ? t.agentNumberRules.unblockType : t.agentNumberRules.blockType}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/*
            What is set, one table per bet type. Agents manage these as separate books,
            and a single list showed only the type whose chip happened to be selected.
          */}
          <div className="mb-2 mt-5 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
              {t.agentNumberRules.myNumbers}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={rules.length === 0 || saving}
              onClick={() => setConfirmClear(true)}
              className="ml-auto h-7 gap-1.5 font-mono text-[11px] text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" /> {t.agentNumberRules.clearAll}
              {rules.length > 0 && <span className="tabular-nums">({rules.length})</span>}
            </Button>
          </div>

          {sections.length === 0 ? (
            <div className="rounded-[var(--radius)] border border-border bg-card p-8 text-center shadow-[var(--shadow-sm)]">
              <p className="font-mono text-xs text-muted-foreground">{t.agentNumberRules.empty}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map((sec) => (
                <TypeSection
                  key={sec.betType}
                  t={t}
                  betType={sec.betType}
                  rows={sec.rows}
                  typeRule={sec.typeRule}
                  cfg={houseConfig[sec.betType]}
                  houseCaps={houseCaps}
                  selected={sec.betType === betType}
                  saving={saving}
                  onSelect={() => setBetType(sec.betType)}
                  onToggleBlock={(row) => void commit({
                    betType: sec.betType, number: row.number,
                    blocked: !row.rule?.blocked,
                    maxPerNumber: row.rule?.maxPerNumber ? Number(row.rule.maxPerNumber) : null,
                    payoutRate: row.rule?.payoutRate ? Number(row.rule.payoutRate) : null,
                  }, row.rule)}
                  onDelete={(id) => void remove(id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={confirmClear} onOpenChange={(v) => { if (!clearing && !v) setConfirmClear(false) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm uppercase tracking-wider">
              {t.agentNumberRules.clearTitle}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs leading-relaxed">
              {t.agentNumberRules.clearDesc(rules.length, lottery?.name ?? '')}
            </DialogDescription>
          </DialogHeader>
          {actionError && (
            <div className="flex items-start gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-card p-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
              <p className="font-mono text-xs text-destructive">{actionError}</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline" size="sm" disabled={clearing}
              onClick={() => setConfirmClear(false)}
              className="h-8 font-mono text-xs"
            >
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive" size="sm" disabled={clearing}
              onClick={() => void clearAll()}
              className="h-8 gap-1.5 font-mono text-xs"
            >
              {clearing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              {t.agentNumberRules.clearConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
